<template>
  <div class="pt-12 p-5 sm:py-12 xs:px-12 sm:px-24">

    <core-logo class="max-w-[184px] mx-auto mb-6" />

    <transition name="f-fade">
      <div v-if="!hasAuthenticated">
        <h1 class="text-center font-serif text-3xl sm:text-3xl md:text-4xl mb-6">
          <template v-if="referralSource.includes('share')">{{referrer.name}} has invited you to view shared memories!</template>
          <template v-else-if="referralSource.includes('collaborate')">{{referrer.name}} has invited you to upload!</template>
          <template v-else-if="referralSource === 'connection-add'">Connect with {{ referrer.name }} on FOREVER!</template>
          <template v-else-if="referralSource === 'connection-link'">Connect with {{ referrer.name }} on FOREVER!</template>
          <template v-else>Welcome to FOREVER!</template>
        </h1>

        <div class="flex flex-col items-center mb-6 gap-3 rounded-lg border border-gray-200 p-5 sm:py-8 sm:px-12" v-if="!referralSource.includes('connection')">
          <user-avatar :user="mappedReferrer" size="4xl" class="mx-auto bg-shell-600" />
          <p class="text-lg font-semibold">{{referrer.name}}</p>
          <u-button
            size="lg"
            color="gray"
            block
            @click="modal.close()"
            class="text-lg"
            label="View Shared Memories"
          />
        </div>

        <div v-else class="flex justify-center">
          <user-avatar :user="mappedReferrer" size="4xl" class="mx-auto bg-shell-600" />
        </div>

        <div class="text-center" v-if="!referralSource.includes('connection')">
          <u-divider class="my-6 text-gray-400" label="or" />
          <p class="text-base sm:text-lg mb-6" v-if="referralSource.includes('share')">Sign in or create your own FOREVER<sub class="reg-mark">®</sub> Account.</p>
          <p class="text-base sm:text-lg mb-6" v-else>Sign in or create your own FOREVER<sub class="reg-mark">®</sub> Account to contribute memories.</p>
        </div>

        <p class="my-6 text-center text-lg" v-else-if="referralSource === 'connection-link'">
          FOREVER is your private, permanent home for memories. Sign up free to connect with {{referrer.name}} and see what’s been shared with you.
        </p>

        <p class="my-6 text-center" v-else>
          To connect with {{referrer.name}}, sign in or create a free FOREVER<sub class="reg-mark">®</sub> Account. Once you’re in, we'll send the request automatically.
        </p>

        <div>
          <user-account-auth
            :default-view="ACCOUNT_AUTH_VIEWS.SIGNUP"
            :referral-source="referralSource"
            :referral-token="referralToken"
            @signup-success="onAuthSuccess"
            @sign-in="onSignIn"
            :signup-cta-text="signupCtaText"
            :login-cta-text="loginCtaText"
            primary-action-variant="solid"
          />
        </div>
      </div>

      <div v-else>
        <template v-if="referralToken">
          <div class="flex flex-col items-center gap-2 text-2xl font-semibold text-charcoal-400 mx-auto text-center my-6">
            <template v-if="isConnected">
              <u-icon name="i-ri-check-double-line" class="text-green-700 text-5xl flex-shrink-0"/>
              Connected with {{referrer.name}}
            </template>
            <template v-else-if="isErrored">
              <u-icon :name="COMMON_ICONS.alert" class="text-orange-700 text-5xl flex-shrink-0"/>
              There was an issue connecting with {{referrer.name}}. You can send a connection request manually on the next screen.
            </template>
            <template v-else>
              <u-icon :name="COMMON_ICONS.loading" class="animate-spin text-5xl flex-shrink-0"/>
              Connecting with {{referrer.name}}
            </template>
          </div>

          <template v-if="isConnected || isErrored">
            <div class="flex justify-center">
              <u-button size="xl" @click="onConnectComplete">Continue</u-button>
            </div>
          </template>
        </template>

        <template v-else>
          ????? ANY OTHER SCENARIOS HERE??
        </template>

      </div>
    </transition>

  </div>
</template>

<script setup>
  const props = defineProps({
    modal: Object,
    referrer: {
      type: Object,
      required: true
    },
    referralToken: String,
    referralContext: String,
    sendConnectionRequest: Boolean,
    referralSource: {
      type: String,
      required: true
    }
  });

  const router = useRouter();
  const route = useRoute();

  const {isAuthenticated} = storeToRefs(useSessionStore());
  const hasAuthenticated = ref(false);
  function onAuthSuccess() {
    hasAuthenticated.value = true;

    //set this cookie so that we don't show the onboarding modal - see auth.global.js for corresponding code
    useCookie('prevent_onboarding', {
      path: '/'
    }).value = 'true';
  }

  function onSignIn() {
    if (!props.sendConnectionRequest || route.query.connect !== 'true' || !process.client) {
      return;
    }

    sessionStorage.setItem(PROFILE_CONNECTION_INTENT_STORAGE_KEY, JSON.stringify({
      userId: props.referrer.id,
      createdAt: Date.now()
    }));
  }

  const loginCtaText = computed(() => {
    if (props.referralSource.includes('share')) {
      return 'Sign In & View Memories';
    } else if (props.referralSource.includes('collaborate')) {
      return 'Sign In & Contribute Memories';
    } else if (props.referralSource === 'connection-add' || props.referralSource === 'connection-link') {
      return 'Sign In & Connect';
    } else {
      return null;
    }
  });

  const signupCtaText = computed(() => {
    if (props.referralSource.includes('share')) {
      return 'Sign Up & View Memories';
    } else if (props.referralSource.includes('collaborate')) {
      return 'Sign Up & Contribute Memories';
    } else if (props.referralSource === 'connection-add' || props.referralSource === 'connection-link') {
      return 'Sign Up & Connect';
    } else {
      return null;
    }
  });

  const isConnected = ref(false);
  const isErrored = ref(false);

  const mappedReferrer = computed(() => {
    return userMapper(props.referrer)
  })
  async function connectUser() {
    try {
      await useUserApi().createFriendshipFromToken({
        referrer: props.referrer,
        token: props.referralToken
      });
      isConnected.value = true;
    } catch (e) {
      console.log(e, 'error connecting user')
      isErrored.value = true;
    }
  }

  function onConnectComplete() {
    if (isConnected.value) {
      useConfetti().fireworks();
    }
    router.replace({
      path: useBuildRoute().toUserProfile({user: {slug: props.referrer.slug}}),
      query: {
        ...route.query
      }
    });
  }

  function reloadAuthenticatedRoute() {
    const query = {...route.query};
    delete query.connect;

    const target = router.resolve({
      path: route.path,
      query,
      hash: route.hash
    }).href;

    window.location.replace(target);
  }

  watch(hasAuthenticated, async (val) => {
    if (val && props.referralToken) {
      await connectUser();
    } else if (val && props.sendConnectionRequest) {
      try {
        await useUsersStore().addConnection({user: props.referrer});
      } catch (e) {
        useErrorToast().add();
      } finally {
        await props.modal.close();
        reloadAuthenticatedRoute();
      }
    } else if (val) {

      router.replace({ path: route.fullPath, query: {
        ...route.query,
        refresh: true
      }});
    }
  });

  onBeforeMount(async () => {
    if (isAuthenticated.value) {
      hasAuthenticated.value = true;
    }
  })

  onMounted(async () => {
    if (!isAuthenticated.value) {
      //get the ambassador_id for the referrer (the prop can be a partial from a shared content
      let ambassador_id = null;
      try {
        const response = await useUserApi().getUser({id: props.referrer.id});
        ambassador_id = response.ambassador_id;
      } catch (e) {
        console.log(e, 'error fetching ambassador_id')
      }

      useExternalRedirect().setSignupCookie({
        value: props.referrer.slug || props.referrer.id,
        context: props.referralContext
      });

      if (ambassador_id) {
        useExternalRedirect().setAmbassadorCookie(ambassador_id);
      }
    }

    const type = props.referralSource.includes('share') ? 'share' : props.referralSource.includes('collaborate') ? 'collaborate' : 'connection';
    useGtm().trackEvent({
      event: 'core_ui-account_auth_modal-open',
      action: 'click',
      category: 'common',
      label: type
    });
  });
</script>
